import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Descriptions, Drawer, Space, Tag, Typography } from "antd";
import { DashboardTableRecord } from "../DashboardTable";
import { Icon as LegacyIcon } from "@ant-design/compatible";
import dayjs from "dayjs";
import {
  areEquals,
  differenceFormatter,
  volumeFormatter,
} from "services/utils/formatterUtil";
import { useIsMobile } from "hooks/useIsMobile";
import { useMemo } from "react";

function DrawerDashoardDetails({
  record,
  onClose,
}: {
  record?: DashboardTableRecord;
  onClose: () => void;
}) {
  if (!record) return null;

  const { isMobile } = useIsMobile();

  const showMonthly = useMemo(() => {
    const recordDate = dayjs(record?.registeredDate);
    return recordDate.date() === recordDate.daysInMonth();
  }, [record.registeredDate]);
  const mismatchEnt = !areEquals(record._entradasVol, record.totalInputs);
  const mismatchSal = !areEquals(record._salidasVol, record.totalOutputs);
  const mismatchInv = !areEquals(
    record.calculatedFinalVolume,
    record.finalVolume,
  );

  const labelStyle: React.CSSProperties = { fontWeight: 600 };

  const SectionTitle = ({ icon, text }: { icon: string; text: string }) => (
    <div
      style={{
        background: "#fafafa",
        border: "1px solid #f0f0f0",
        borderRadius: 6,
        padding: "6px 12px",
        marginTop: 16,
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      <LegacyIcon type={icon} style={{ color: "#08c", fontSize: 18 }} /> {text}
    </div>
  );

  return (
    <Drawer
      title={
        <Space direction="vertical" size={0}>
          <strong>
            {record.distributionPointSenerLicense ??
              record.distributionPointName}
          </strong>
          {record.distributionPointName && (
            <Typography.Text type="secondary">
              {record.distributionPointName}
            </Typography.Text>
          )}
        </Space>
      }
      placement="right"
      width={isMobile ? "100%" : "50%"}
      open={!!record}
      onClose={onClose}
      styles={{ body: { padding: "12px 20px" } }}
    >
      {/*─── Seccion: Control de Existencias ──────────────────────────────────────────────────────────────────*/}

      <SectionTitle icon="fieldTime" text="Control de Existencias" />
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item
          label={<span style={labelStyle}>Existe control de existencias</span>}
        >
          {record.inventoryControlExist ? (
            <Tag color="success">Sí</Tag>
          ) : (
            <Tag color="error">No</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={<span style={labelStyle}>Tiene informacion tardia</span>}
        >
          {record.hasLateArrivingInfo ? (
            <Tag color="error">Sí</Tag>
          ) : (
            <Tag color="success">No</Tag>
          )}
        </Descriptions.Item>
      </Descriptions>

      {/*─── Seccion de Entradas: Recepciones y Traspasos ──────────────────────────────────────────────────────────────────*/}
      <SectionTitle icon="fullscreenExit" text="Entradas" />

      <Descriptions column={1} size="small" bordered>
        {mismatchEnt && (
          <>
            {/* ─── Total - Control de Existencias ──────────── */}
            <Descriptions.Item
              label={
                <Space style={labelStyle}>
                  Total Entradas
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    (control de existencias)
                  </Typography.Text>
                </Space>
              }
            >
              {volumeFormatter(record.totalInputs)}
            </Descriptions.Item>
          </>
        )}

        {/* ─── Total - Datos Operativos ──────────── */}
        <Descriptions.Item
          label={
            <Space style={labelStyle}>
              Total Entradas
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                (datos operativos)
              </Typography.Text>
            </Space>
          }
        >
          <span>{volumeFormatter(record._entradasVol)}</span>
        </Descriptions.Item>

        {/* ─── Recepciones - Datos Operativos ──────────── */}

        <Descriptions.Item
          label={<span style={{ paddingLeft: 16 }}>↳ Recepciones</span>}
        >
          {volumeFormatter(record.calculatedTotalReceptions)}
        </Descriptions.Item>

        {/* ─── Traspasos - Datos Operativos ──────────── */}

        <Descriptions.Item
          label={<span style={{ paddingLeft: 16 }}>↳ Traspasos</span>}
        >
          {volumeFormatter(record.calculatedTotalTraspasos)}
        </Descriptions.Item>

        {/* ─── Total CFDI - Datos Operativos ──────────── */}
        <Descriptions.Item
          label={
            <Space style={labelStyle}>
              Total Facturado (CFDIs)
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                (datos operativos)
              </Typography.Text>
            </Space>
          }
        >
          {volumeFormatter(record._entradasCfdi)}
        </Descriptions.Item>

        {/* ─── Recepciones CFDI - Datos Operativos ──────────── */}
        <Descriptions.Item
          label={<span style={{ paddingLeft: 16 }}>↳ CFDIs Recepciones</span>}
        >
          {volumeFormatter(record.calculatedTotalCfdiReceptions)}
        </Descriptions.Item>

        {/* ─── Traspasos CFDI - Datos Operativos ──────────── */}
        <Descriptions.Item
          label={<span style={{ paddingLeft: 16 }}>↳ CFDIs Traspasos</span>}
        >
          {volumeFormatter(record.calculatedTotalCfdiTraspasos)}
        </Descriptions.Item>
      </Descriptions>

      {/*─── Seccion de Salidas: Ventas ──────────────────────────────────────────────────────────────────*/}
      <SectionTitle icon="fullscreen" text="Salidas" />
      <Descriptions column={1} size="small" bordered>
        {/* ─── Total - Control de Existencias ──────────── */}
        {mismatchSal && (
          <>
            <Descriptions.Item
              label={
                <Space style={labelStyle}>
                  Total Ventas
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    (control de existencias)
                  </Typography.Text>
                </Space>
              }
            >
              {volumeFormatter(record.totalOutputs)}
            </Descriptions.Item>
          </>
        )}

        {/* ─── Total - Datos Operativos ──────────── */}
        <Descriptions.Item
          label={
            <Space style={labelStyle}>
              Total Ventas
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                (datos operativos)
              </Typography.Text>
            </Space>
          }
        >
          {volumeFormatter(record._salidasVol)}
        </Descriptions.Item>

        {/* ─── Total CFDI - Datos Operativos ──────────── */}
        <Descriptions.Item
          label={<span style={{ paddingLeft: 16 }}>↳ CFDIs Ventas</span>}
        >
          {volumeFormatter(record.calculatedTotalCfdiSales)}
        </Descriptions.Item>
      </Descriptions>

      {/*─── Seccion Inventario - Mediciones de Volumen ──────────────────────────────────────────────────────────────────*/}
      <SectionTitle icon="control" text="Inventario" />

      {/* ─── Volumen inicial - Control de Existencias ──────────── */}
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item
          label={
            <Space style={labelStyle}>
              Volumen inicial
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                (control de existencias)
              </Typography.Text>
            </Space>
          }
        >
          {volumeFormatter(record.initialVolume)}
        </Descriptions.Item>

        {/* ─── Diferencia - Control de existencias ──────────── */}
        {mismatchInv && (
          <>
            <Descriptions.Item
              label={
                <Space style={labelStyle}>
                  Diferencia
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    (control de existencias)
                  </Typography.Text>
                </Space>
              }
            >
              {differenceFormatter(
                record.volumeDifference,
                record.percentageDifference,
              )}
            </Descriptions.Item>

            {/* ─── Volumen Calculado - Control de existencias ──────────── */}

            <Descriptions.Item
              label={
                <span style={{ paddingLeft: 16 }}>↳ Volumen Calculado</span>
              }
            >
              {volumeFormatter(record.finalVolume)}
            </Descriptions.Item>

            {/* ─── Volumen Fisico - Control de existencias ──────────── */}
            <Descriptions.Item
              label={<span style={{ paddingLeft: 16 }}>↳ Volumen Fisico</span>}
            >
              {volumeFormatter(record.finalStaticVolume)}
            </Descriptions.Item>
          </>
        )}

        {/* ─── Volumen Calculado - Datos Operativos ──────────── */}
        <Descriptions.Item
          label={
            <Space style={labelStyle}>
              Diferencia
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                (datos operativos)
              </Typography.Text>
            </Space>
          }
        >
          {differenceFormatter(
            record.calculatedVolumeDifference,
            record.calculatedPercentageDifference,
          )}
        </Descriptions.Item>

        {/* ─── Volumen Calculado - Datos Operativos ──────────── */}
        <Descriptions.Item
          label={<span style={{ paddingLeft: 16 }}>↳ Volumen Calculado</span>}
        >
          {volumeFormatter(record.calculatedFinalVolume)}
        </Descriptions.Item>

        {/* ─── Volumen Fisico - Datos Operativos ──────────── */}
        <Descriptions.Item
          label={<span style={{ paddingLeft: 16 }}>↳ Volumen Fisico</span>}
        >
          {volumeFormatter(record.calculatedLatestMeasurementVolume)}
        </Descriptions.Item>
      </Descriptions>

      {/*─── Seccion: Json ──────────────────────────────────────────────────────────────────*/}

      <SectionTitle icon="bank" text="Reporte JSON" />
      <Descriptions column={1} size="small" bordered>
        {/* ─── Diarios ──────────── */}
        <Descriptions.Item label={<span style={labelStyle}>JSON Diario</span>}>
          {!record.dailyJsonExist ? (
            <Tag color="error">No existe reporte JSON</Tag>
          ) : record.dailyJsonError ? (
            <Tag color="error" icon={<CloseCircleOutlined />}>
              El reporte Json tiene errores
            </Tag>
          ) : (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              OK
            </Tag>
          )}
        </Descriptions.Item>
        {record.dailyJsonError && (
          <Descriptions.Item
            label={<span style={labelStyle}>Detalle error diario</span>}
          >
            <pre
              style={{
                background: "#fff1f0",
                border: "1px solid #ffa39e",
                borderRadius: 4,
                padding: "8px 10px",
                fontSize: 11,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                margin: 0,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {record.dailyJsonError}
            </pre>
          </Descriptions.Item>
        )}

        {/* ─── Mensual ──────────── */}

        {showMonthly && (
          <>
            <Descriptions.Item
              label={<span style={labelStyle}>JSON Mensual</span>}
            >
              {!record.monthlyJsonExist ? (
                <Tag color="error">No existe reporte JSON</Tag>
              ) : record.monthlyJsonError ? (
                <Tag color="error" icon={<CloseCircleOutlined />}>
                  El reporte Json tiene errores
                </Tag>
              ) : (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  OK
                </Tag>
              )}
            </Descriptions.Item>
            {record.monthlyJsonError && (
              <Descriptions.Item
                label={<span style={labelStyle}>Detalle error mensual</span>}
              >
                <pre
                  style={{
                    background: "#fff1f0",
                    border: "1px solid #ffa39e",
                    borderRadius: 4,
                    padding: "8px 10px",
                    fontSize: 11,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    margin: 0,
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  {record.monthlyJsonError}
                </pre>
              </Descriptions.Item>
            )}
          </>
        )}
        <Descriptions.Item
          label={<span style={labelStyle}>Enviado al SAT</span>}
        >
          {record.isSentToSat ? (
            <Tag color="error">Sí</Tag>
          ) : (
            <Tag color="success">No</Tag>
          )}
        </Descriptions.Item>
      </Descriptions>
    </Drawer>
  );
}

export default DrawerDashoardDetails;
